import { useState, useEffect, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useLocationsDataReady } from './useLocationsDataReady';

const LOG_PREFIX = '[ws-locations-control][useLocationsData]';

/**
 * Hook to integrate with ws-locations-data widget.
 * Uses the global window.DDC.LocationsData API to:
 * 1. Listen for LocationsData availability via custom event
 * 2. Subscribe to location updates via registerLocationsNotification
 * 3. Trigger location searches via setSearchParams
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPostalCode, setCurrentPostalCode] = useState(null);

	// hook to check if LocationsData is ready
	const { isReady, error: readyError } = useLocationsDataReady();

	// Set error if LocationsData failed to initialize
	useEffect(() => {
		if (readyError) {
			setError(readyError);
			setIsLoading(false);
		}
	}, [readyError]);

	// Subscribe to location updates
	useEffect(() => {
		if (!isReady) {
			logger.debug(`${LOG_PREFIX} Not subscribing - isReady: ${isReady}`);
			return () => {};
		}

		const handleLocationsUpdate = () => {
			logger.debug(`${LOG_PREFIX} Received locations update`);

			const locationsData = window.DDC.LocationsData;
			const newLocations = locationsData.getLocations() || [];
			const newError = locationsData.getError();

			setLocations(newLocations);
			setError(newError);
			setIsLoading(false);

			logger.debug(
				`${LOG_PREFIX} Updated state - count: ${
					newLocations.length
				}, hasError: ${!!newError}`
			);
		};

		// Register for notifications
		logger.debug(`${LOG_PREFIX} Registering for location notifications`);
		const unsubscribe =
			window.DDC.LocationsData.registerLocationsNotification(
				handleLocationsUpdate
			);

		// Cleanup subscription on unmount
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing from notifications`);
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [isReady]);

	// Function to trigger location search via ws-locations-data
	const setSearchParams = useCallback(
		(postalCode, range, excludeByRange) => {
			if (!isReady || !window.DDC?.LocationsData) {
				logger.warn(
					`${LOG_PREFIX} Cannot set postal code - LocationsData not ready`
				);
				return;
			}

			logger.debug(`${LOG_PREFIX} Setting postal code: ${postalCode}`, {
				range,
				excludeByRange
			});
			setIsLoading(true);
			setError(null);
			setCurrentPostalCode(postalCode);
			window.DDC.LocationsData.setSearchParams(
				postalCode,
				range,
				excludeByRange
			);
		},
		[isReady]
	);

	return {
		locations,
		locationCount: locations.length,
		error,
		isReady,
		isLoading,
		currentPostalCode,
		setSearchParams
	};
};
